import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChevronRight, Heart, Shield } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import type { Maintainer } from '../../types/maintainer';

interface CragMaintainersCardProps {
  maintainers: Maintainer[];
}

/**
 * Who looks after the crag, on its info tab.
 *
 * A logo and a name and nothing else: the point of the row is to be recognised
 * — most of these are local clubs whose sticker is on the approach sign — and
 * everything else about them is one tap away on their own page.
 *
 * The one exception is the donate button, which sits outside the link rather
 * than inside it: it goes somewhere else entirely (the club's own donation
 * page, in a new tab), and an anchor cannot be nested in an anchor. Only clubs
 * that have given us a `donation_url` get one — most have none.
 *
 * Renders nothing when nobody is listed. A crag with no maintainer is the
 * ordinary case, and an empty "nobody looks after this" card would be a claim
 * we cannot actually make.
 */
export function CragMaintainersCard({ maintainers }: CragMaintainersCardProps) {
  const { t } = useTranslation();

  if (maintainers.length === 0) return null;

  return (
    <Card>
      <CardContent className="space-y-2 p-4">
        <h3 className="flex items-center gap-1.5 text-sm font-medium">
          <Shield className="h-4 w-4" />
          {t('cragMaintainers.title')}
        </h3>

        <ul className="-mx-4 divide-y border-y">
          {maintainers.map((maintainer) => (
            <li key={maintainer.id} className="flex items-center">
              <Link
                to={`/maintainers/${maintainer.slug}`}
                className="flex min-w-0 flex-1 items-center gap-3 px-4 py-2.5 transition-colors hover:bg-accent"
              >
                <Avatar className="h-9 w-9">
                  {(maintainer.logo_small_url || maintainer.logo_url) && (
                    <AvatarImage
                      src={maintainer.logo_small_url || maintainer.logo_url || undefined}
                      alt={maintainer.name}
                      className="object-contain"
                    />
                  )}
                  <AvatarFallback className="text-xs">
                    {maintainer.name.slice(0, 2).toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <span className="min-w-0 flex-1 truncate text-sm font-medium">{maintainer.name}</span>
                <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
              </Link>

              {maintainer.donation_url && (
                <Button asChild size="sm" className="mr-4 shrink-0 gap-1.5">
                  <a href={maintainer.donation_url} target="_blank" rel="noopener noreferrer">
                    <Heart className="h-3.5 w-3.5" />
                    {t('maintainer.donate')}
                  </a>
                </Button>
              )}
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
}
